import React from 'react';
import cn from 'classnames';
import {t} from '@lingui/core/macro';

import {MonitorMenu, MonitorRowStatusCell, MonitorTagsModal} from 'entities/monitor';
import {ExceededModal, IUserOutput} from 'entities/user';
import Edit from 'shared/ui/icons/General/Edit';
import StatusToggle from 'shared/ui/StatusToggle/StatusToggle';
import RowTags from 'shared/ui/Table/RowTags/RowTags';
import {IPortMonitorListItem} from 'features/port/model/PortMonitor/IPortMonitor';
import usePortMonitorRow from 'features/port/model/usePortMonitorRow';
import {getFormattedDate} from 'shared/lib/datetime';
import {getPortDesiredStateLabel} from 'features/port/lib/portMonitorHelper';


import useUpdatePortMonitorTags from 'features/port/model/useUpdatePortMonitorTags';

import PortMonitorModal from '../PortMonitorModal';
import PortMonitorStateBadge from '../PortMonitorStateBadge/PortMonitorStateBadge';
import PortMonitorDeleteModal from '../PortMonitorDeleteModal/PortMonitorDeleteModal';
import PortMonitorPauseModal from '../PortMonitorPauseModal/PortMonitorPauseModal';
import PortMonitorResumeModal from '../PortMonitorResumeModal/PortMonitorResumeModal';

import styles from './PortMonitorRow.module.scss';

interface IPortMonitorRowProps {
    item: IPortMonitorListItem,
    user: IUserOutput | null,
    hasSelectedItems: boolean,
    isMassOperationsMode: boolean,
}

const PortMonitorRow: React.FC<IPortMonitorRowProps> = ({
    item,
    user,
    hasSelectedItems,
    isMassOperationsMode,
}) => {
    const {
        activeModal,
        closeModal,
        canCreate,
        isSelected,
        isPaused,
        name,
        address,
        tags,
        dateFormat,
        onCheckboxChange,
        onTagClick,
        onDetailsClick,
        onToggleClick,
        onCheckClick,
        onEditClick,
        onTagsClick,
        onPauseClick,
        onResumeClick,
        onDeleteClick,
    } = usePortMonitorRow(item);

    return (
        <>
            <tr
                className={cn(
                    styles.row,
                    isPaused && styles.isPaused,
                )}
                onClick={onDetailsClick}
            >
                <MonitorRowStatusCell
                    isMassOperationsMode={isMassOperationsMode}
                    hasSelectedItems={hasSelectedItems}
                    isSelected={isSelected}
                    onCheckboxChange={onCheckboxChange}
                    isPaused={isPaused}
                    ok={item.ok}
                    checkboxClassName={styles.checkbox}
                />
                <td className={styles.blockName}>
                    <div className={styles.containerName}>
                        <span className={styles.name} title={name}>{name}</span>
                        <span className={styles.editIcon} onClick={onEditClick}>
                            <Edit/>
                        </span>
                    </div>
                    {name !== address && <span className={styles.address}>{address}</span>}
                    {tags.length > 0 && (
                        <RowTags tags={tags} onTagClick={onTagClick} disabled={hasSelectedItems}/>
                    )}
                </td>
                <td className={styles.stateCell} onClick={(e) => e.stopPropagation()}>
                    <PortMonitorStateBadge status={item.status} ok={item.ok}/>
                </td>
                <td>{getPortDesiredStateLabel(item.desired_state)}</td>
                <td>{item.check_interval} {t`мин.`}</td>
                <td className={styles.dateCell}>
                    {getFormattedDate(item.last_check, {
                        format: dateFormat,
                        tzString: user?.timezone,
                        nullValue: '—',
                    })}
                </td>
                <td className={styles.toggleCell}>
                    <StatusToggle
                        condition={!isPaused}
                        onClick={onToggleClick}
                        notActiveText={t`На паузе`}
                        preventToggle={isPaused && !canCreate}
                    />
                </td>
                <td onClick={(e) => e.stopPropagation()}>
                    <div className="d-flex justify-content-center">
                        <MonitorMenu
                            isActive={!isPaused}
                            onCheckClick={onCheckClick}
                            onEditClick={onEditClick}
                            onDetailsClick={onDetailsClick}
                            onTagsClick={onTagsClick}
                            onPauseClick={onPauseClick}
                            onResumeClick={onResumeClick}
                            onDeleteClick={onDeleteClick}
                        />
                    </div>
                </td>
            </tr>

            <PortMonitorModal
                item={item}
                open={activeModal === 'edit'}
                handleClose={closeModal}
            />

            <PortMonitorPauseModal
                item={item}
                open={activeModal === 'pause'}
                handleClose={closeModal}
            />

            <PortMonitorResumeModal
                item={item}
                open={activeModal === 'resume'}
                handleClose={closeModal}
            />

            <PortMonitorDeleteModal
                item={item}
                open={activeModal === 'delete'}
                handleClose={closeModal}
            />

            <MonitorTagsModal
                uuid={item.uuid}
                tags={item.tags}
                useUpdateTags={useUpdatePortMonitorTags}
                open={activeModal === 'tags'}
                handleClose={closeModal}
            />

            <ExceededModal
                isOpen={activeModal === 'limit'}
                handleClose={closeModal}
                type="port_monitors"
            />
        </>
    );
};

export default PortMonitorRow;
